/**
 * Temel analiz değerlerini hesaplar.
 * @param {Object} ozetRapor - Özet rapor
 * @returns {Object|null} Temel analiz değerleri veya null (eğer veri eksikse)
 */
export const generateTemelAnalizDegerleri = (ozetRapor) => {
  // Null kontrolü
  if (
    !ozetRapor ||
    !ozetRapor.tarihselFiyat ||
    !ozetRapor.bilanco.anaOrtaklikOzKaynaklar ||
    !ozetRapor.bilanco.anaOrtaklikOzKaynaklar.length ||
    !ozetRapor.bilanco.odenmisSermaye ||
    !ozetRapor.bilanco.odenmisSermaye.length ||
    // !ozetRapor.sermayeArtirimlari ||
    !ozetRapor.sirketBilgileri ||
    !ozetRapor.gelirTablosu.anaOrtaklikPaylari ||
    !ozetRapor.gelirTablosu.anaOrtaklikPaylari.length ||
    !ozetRapor.gelirTablosu.faaliyetKari ||
    !ozetRapor.gelirTablosu.faaliyetKari.length ||
    !ozetRapor.gelirTablosu.donemKari ||
    !ozetRapor.gelirTablosu.donemKari.length
  ) {
    console.error("Temel analiz değerleri hesaplanamadı: Eksik veri");
    return null;
  }

  // Dönem bilgisi
  const year = ozetRapor.bilanco.anaOrtaklikOzKaynaklar[0].year;
  const month = ozetRapor.bilanco.anaOrtaklikOzKaynaklar[0].month;
  const donem = `${year}/${month}`;

  // Hisse Fiyatı
  const hisseFiyati = ozetRapor.tarihselFiyat.Close;

  // Özkaynaklar
  const ozkaynaklar = ozetRapor.bilanco.anaOrtaklikOzKaynaklar[0].value;

  // Ödenmiş Sermaye
  let odenmisSermaye;
  const odenmisSermayeA = ozetRapor.bilanco.odenmisSermaye[0].value;

  let odenmisSermayeB = 0;
  if (
    ozetRapor.sermayeArtirimlari &&
    ozetRapor.sermayeArtirimlari["Bölünme Sonrası Sermaye (TL)"] &&
    ozetRapor.sermayeArtirimlari["Tarih"] &&
    ozetRapor.sermayeArtirimlari["Tarih"] !== "Onay bekliyor" &&
    /^\d{2}-\d{2}-\d{4}$/.test(ozetRapor.sermayeArtirimlari["Tarih"])
  ) {
    odenmisSermayeB = parseFloat(
      ozetRapor.sermayeArtirimlari["Bölünme Sonrası Sermaye (TL)"].replace(
        /\./g,
        ""
      )
    );
  }

  let odenmisSermayeC = 0;
  if (ozetRapor.sirketBilgileri && Array.isArray(ozetRapor.sirketBilgileri)) {
    const toplam = ozetRapor.sirketBilgileri.find(
      (item) => item["Ortağın Adı-Soyadı/Ticaret Unvanı"] === "TOPLAM"
    );
    if (toplam && toplam["Sermayedeki Payı (TL)"]) {
      odenmisSermayeC = parseFloat(
        toplam["Sermayedeki Payı (TL)"].replace(/\./g, "")
      );
    }
  }

  odenmisSermaye = Math.max(
    odenmisSermayeA || 0,
    odenmisSermayeB || 0,
    odenmisSermayeC || 0
  );

  // Satış Gelirleri
  const satisGelirleri = ozetRapor.gelirTablosu.satisGelirleri[0].ttm_value;

  // Brüt Kar (Zarar)
  const brutKar = ozetRapor.gelirTablosu.brutKar[0].ttm_value;

  // Net Dönem Karı
  const netDonemKari = ozetRapor.gelirTablosu.anaOrtaklikPaylari[0].ttm_value;

  // Faaliyet Karı
  const faaliyetKari = ozetRapor.gelirTablosu.faaliyetKari[0].ttm_value;

  // Son İki Dönem Karı
  let sonIkiDonemKari = 0;
  if (ozetRapor.gelirTablosu.donemKari.length >= 2) {
    sonIkiDonemKari =
      ozetRapor.gelirTablosu.donemKari[0].quarter_value +
      ozetRapor.gelirTablosu.donemKari[1].quarter_value;
  } else if (ozetRapor.gelirTablosu.donemKari.length === 1) {
    sonIkiDonemKari = ozetRapor.gelirTablosu.donemKari[0].quarter_value;
  }

  // FK (Fiyat/Kar)
  const FK =
    !isNaN(hisseFiyati) &&
    !isNaN(netDonemKari) &&
    !isNaN(odenmisSermaye) &&
    odenmisSermaye !== 0 &&
    netDonemKari !== 0
      ? parseFloat((hisseFiyati / (netDonemKari / odenmisSermaye)).toFixed(2))
      : 0;

  // PDDD (Piyasa Değeri / Defter Değeri)
  const PDDD =
    !isNaN(odenmisSermaye) &&
    !isNaN(hisseFiyati) &&
    !isNaN(ozkaynaklar) &&
    ozkaynaklar !== 0
      ? parseFloat(((odenmisSermaye * hisseFiyati) / ozkaynaklar).toFixed(2))
      : 0;

  // HBK (Hisse Başına Kar)
  const HBK =
    !isNaN(netDonemKari) && !isNaN(odenmisSermaye) && odenmisSermaye !== 0
      ? parseFloat((netDonemKari / odenmisSermaye).toFixed(2))
      : 0;

  // HBK Ederi (Hisse Başına Kar)
  const HBK_Ederi =
    !isNaN(netDonemKari) && !isNaN(odenmisSermaye) && odenmisSermaye !== 0
      ? parseFloat(((netDonemKari / odenmisSermaye) * 10).toFixed(2))
      : 0;

  // EFK Ederi (Ekonomik Fayda Karı)
  const EFK_Ederi =
    !isNaN(faaliyetKari) &&
    !isNaN(ozkaynaklar) &&
    !isNaN(odenmisSermaye) &&
    odenmisSermaye !== 0
      ? parseFloat(
          ((faaliyetKari * 7 + ozkaynaklar / 2) / odenmisSermaye).toFixed(2)
        )
      : 0;

  // KAR Ederi (Karşılaştırmalı Analiz Rasyosu)
  const KAR_Ederi =
    !isNaN(sonIkiDonemKari) && !isNaN(odenmisSermaye) && odenmisSermaye !== 0
      ? parseFloat((((sonIkiDonemKari * 2) / odenmisSermaye) * 10).toFixed(2))
      : 0;

  // Hedef Fiyat
  const Hedef_Fiyat =
    !isNaN(HBK_Ederi) && !isNaN(EFK_Ederi) && !isNaN(KAR_Ederi)
      ? parseFloat(((HBK_Ederi + EFK_Ederi + KAR_Ederi) / 3).toFixed(2))
      : 0;

  // Potansiyel
  const Potansiyel =
    !isNaN(Hedef_Fiyat) && !isNaN(hisseFiyati) && hisseFiyati !== 0
      ? parseFloat(
          (((Hedef_Fiyat - hisseFiyati) / hisseFiyati) * 100).toFixed(2)
        )
      : 0;

  // Sonuçları döndür
  return {
    donem,
    hisseFiyati,
    ozkaynaklar,
    odenmisSermaye,
    satisGelirleri,
    brutKar,
    netDonemKari,
    faaliyetKari,
    sonIkiDonemKari,
    FK,
    PDDD,
    HBK,
    HBK_Ederi,
    EFK_Ederi,
    KAR_Ederi,
    Hedef_Fiyat,
    Potansiyel,
  };
};
